import moment from 'moment'
import { useState, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useLocation, useParams } from 'react-router-dom'

import { CONNECTION_TYPES } from 'api/schedule_types/constants'

import { selectDashboardStatus } from 'slices/dashboardSlice'
import { getScheduleTypeList, getRelatedScheduleType, selectScheduleTypesStatus } from 'slices/scheduleTypesSlice'
import { getTenantWithDate } from 'slices/tenantsSlice'
import { getDisplayFilter } from 'slices/usersSlice'
import { getWorkspaceList } from 'slices/workspacesSlice'

import { BadgeLabel, DropdownButton, MoveDropdown, NavMenu } from 'components/common'
import { TIME_INTERVAL } from 'components/common/constants'

import useDateQuery from 'hooks/useDateQuery'

import CSVManualRecordsInputDialog from './CSVManualRecordsInputDialog'
import DashboardDateChangeButton from './DashboardDateChangeButton'
import ManualInputDialog from './ManualInputDialog/ManualInputDialog'

import type { Moment } from 'moment'
import type { ReactNode } from 'react'

const MANUAL_INPUT_AVAILABLE_PERIOD = 32

type Props = {
  onInterval: () => void
  onDateChange: (newDate: string) => void
  children: ReactNode
}

const Dashboard = ({ children, onInterval, onDateChange }: Props) => {
  const [openMoment, setOpenMoment] = useState<Moment>(moment())
  const { workspaceId } = useParams<'workspaceId'>()
  const [isOpenManualInputDialog, setIsOpenManualInputDialog] = useState(false)
  const [isOpenCSVManualRecordsInputDialog, setIsOpenCSVManualRecordsInputDialog] = useState(false)
  const dispatch = useDispatch()
  const { pathname } = useLocation()
  const date = useDateQuery()

  const { tenantSummary } = useSelector(selectDashboardStatus, shallowEqual)
  const { allScheduleTypes, partialScheduleTypes } = useSelector(selectScheduleTypesStatus, shallowEqual)

  const isBopMonitoring = useMemo(() => pathname === '/dashboard/balance-of-payments', [pathname])

  const workspaceName = useMemo(
    () => tenantSummary?.workspaceData.find(data => data.workspaceId === Number(workspaceId))?.workspaceName || '',
    [tenantSummary?.workspaceData, workspaceId]
  )

  useEffect(() => {
    dispatch(getDisplayFilter())
  }, [dispatch])

  useEffect(() => {
    // レスポンスはManualInputDialogとCSVManualRecordsInputDialogでも使用
    dispatch(getWorkspaceList({ workDate: date }))
    // individualSummaryとTotalSummaryでtenantWithDateを使用
    dispatch(getTenantWithDate(date))
  }, [dispatch, date])

  useEffect(() => {
    // レスポンスはManualInputDialogとCSVManualRecordsInputDialogでも使用
    if (!workspaceId) {
      dispatch(getRelatedScheduleType(date))
      return
    }
    dispatch(getScheduleTypeList(Number(workspaceId), date))
  }, [dispatch, workspaceId, date])

  useEffect(() => {
    const timerId = setTimeout(
      () => {
        onInterval()
        setOpenMoment(moment())
      },
      TIME_INTERVAL.FIVE * 60 * 1000
    )
    return () => clearTimeout(timerId)
  }, [dispatch, openMoment, date, onInterval])

  const showManualInput = useMemo(() => {
    if (isBopMonitoring) {
      return false
    }

    const existManualInput = workspaceId
      ? partialScheduleTypes.some(type => type.connectionType === CONNECTION_TYPES.MANUAL)
      : allScheduleTypes.some(type => type.connectionType === CONNECTION_TYPES.MANUAL)
    const today = moment().startOf('d')
    const diff = moment(today).diff(date, 'd')
    const inPeriod = diff >= 0 && diff < MANUAL_INPUT_AVAILABLE_PERIOD

    return existManualInput && inPeriod
  }, [isBopMonitoring, date, allScheduleTypes, partialScheduleTypes, workspaceId])

  const handleManualInputSuccess = () => {
    setIsOpenManualInputDialog(false)
    onInterval()
  }
  const handleCSVManualRecordsInputSuccess = () => {
    setIsOpenCSVManualRecordsInputDialog(false)
    onInterval()
  }

  const badgeLabel = useMemo(() => {
    if (isBopMonitoring) {
      return '収支モニタリング'
    }
    if (workspaceId) {
      return workspaceName
    }
    return '業務モニタリング'
  }, [isBopMonitoring, workspaceId, workspaceName])

  return (
    <NavMenu>
      <div>
        <div className="d-flex m-3">
          <div className="d-flex align-items-center">
            <div className="font-x-large fw-bold pe-2">ダッシュボード</div>
            <BadgeLabel label={badgeLabel} />
          </div>
          <div className="d-flex ms-auto">
            {workspaceId && <MoveDropdown className="me-2" />}
            {showManualInput && (
              <DropdownButton
                buttonLabel="実績入力"
                onClickButton={() => setIsOpenManualInputDialog(true)}
                dropdownItems={[
                  { label: '実績CSV一括入力', onClick: () => setIsOpenCSVManualRecordsInputDialog(true) },
                ]}
                className="me-2"
              />
            )}
            <DashboardDateChangeButton onChange={onDateChange} />
          </div>
        </div>
        <div key={`auto-rerender-${openMoment}`} className="mt-3 mx-3">
          {children}
        </div>
        <ManualInputDialog
          isOpen={isOpenManualInputDialog}
          onCancel={() => setIsOpenManualInputDialog(false)}
          onSuccess={handleManualInputSuccess}
        />
        <CSVManualRecordsInputDialog
          isOpen={isOpenCSVManualRecordsInputDialog}
          onCancel={() => setIsOpenCSVManualRecordsInputDialog(false)}
          date={date}
          onSuccess={handleCSVManualRecordsInputSuccess}
        />
      </div>
    </NavMenu>
  )
}

export default Dashboard
